import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Home, MapPin, Package, Loader2, Info, Store, ExternalLink } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../lib/AuthContext';
import { useCart } from '../hooks/useCart';
import { useLanguage, useT } from '../lib/i18n';
import { formatCurrency } from '../lib/currency';
import { checkoutApi, accountApi, cartApi, prescribersApi, publicFlagsApi } from '../api/client';
import { BpostLocationSearch } from '../components/checkout/BpostLocationSearch';

const inputClass = 'w-full px-3.5 py-2.5 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-shadow';

// `available` is the static (pre-runtime-flag) default. Once the
// public flag `pickupLockerCustomerVisible` lands, PICKUP_POINT and
// PARCEL_LOCKER become customer-selectable for BE addresses only.
// Non-BE customers are filtered out elsewhere via `bpostCountrySupported`.
const DELIVERY_OPTIONS = [
  {
    value: 'HOME',
    label: 'Thuislevering',
    description: 'Levering op het opgegeven adres.',
    Icon: Home,
    available: true,
  },
  {
    value: 'PICKUP_POINT',
    label: 'Afhaalpunt',
    description: 'Levering in een bpost-afhaalpunt.',
    Icon: MapPin,
    available: false, // runtime-flag driven: see `pickupLockerVisible` below
  },
  {
    value: 'PARCEL_LOCKER',
    label: 'bpost Pakjesautomaat',
    description: 'Levering in een pakjesautomaat.',
    Icon: Package,
    available: false,
  },
];

function Field({ label, error, children, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
      {error && <p className="text-xs text-destructive mt-1">{error.message || 'Dit veld is verplicht'}</p>}
    </div>
  );
}

export default function Checkout() {
  const { user } = useAuth();
  const { locale } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const { items, loading: cartLoading, clearCart, reload: reloadCart } = useCart();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Legal acceptance gate — required before payment can start.
  // Pre-launch compliance (2026-05-19): the customer must explicitly
  // confirm the terms / privacy / withdrawal / shipping pages have
  // been read. The submit button is hardened to refuse to fire as
  // long as this checkbox is unchecked; the same flag is checked
  // inside `onSubmit` as a defence-in-depth gate.
  const [legalAccepted, setLegalAccepted] = useState(false);
  // P0-4: structured cart-changed banner. `null` when no drift,
  // otherwise the server's 409 detail `{code, message, items[]}`.
  const [cartDiff, setCartDiff] = useState(null);
  const [discountInfo, setDiscountInfo] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState('HOME');
  // bpost validation state — optional, gated on backend availability.
  const [bpostAvailable, setBpostAvailable] = useState(false);
  const [bpostBusy, setBpostBusy] = useState(false);
  const [bpostSuggestion, setBpostSuggestion] = useState(null);
  const [bpostStatus, setBpostStatus] = useState(null);

  // Pickup / locker manual entry is only available in explicit test
  // mode (URL `?testMode=1`) or for admin users. Customers see a
  // disabled "Binnenkort beschikbaar" card until the real bpost
  // selector is wired.
  const testMode = new URLSearchParams(location.search).get('testMode') === '1';
  const manualPickupAllowed = testMode || user?.role === 'admin';

  // Runtime feature flag — `pickupLockerCustomerVisible`. When true,
  // BE customers can also select PICKUP_POINT / PARCEL_LOCKER. Stays
  // false by default until the operator flips the flag via the
  // `/api/admin/system/pickup-locker/toggle` endpoint. Failures fall
  // back to safe-off — no extra options are shown.
  const [pickupLockerVisible, setPickupLockerVisible] = useState(false);
  // Pre-cutover commerce lock (2026-06-18). Tri-state: null = flags
  // still loading (spinner), false = commerce NOT live → render the
  // pre-cutover notice instead of a functioning checkout (also for
  // direct /checkout visits), true = normal checkout. Fail-closed:
  // a flags fetch error keeps checkout disabled. The backend
  // re-enforces this with HTTP 503 `commerce_not_live` so a direct
  // API call is refused with zero side effects either way.
  const [commerceLive, setCommerceLive] = useState(null);
  useEffect(() => {
    let mounted = true;
    publicFlagsApi.fetch()
      .then(({ data }) => {
        if (mounted) {
          setPickupLockerVisible(Boolean(data?.pickupLockerCustomerVisible));
          setCommerceLive(data?.commerceLive === true);
        }
      })
      .catch(() => {
        // Safe-off on any error.
        if (mounted) {
          setPickupLockerVisible(false);
          setCommerceLive(false);
        }
      });
    return () => { mounted = false; };
  }, []);

  const { register, handleSubmit, setValue, getValues, watch, formState: { errors } } = useForm({
    defaultValues: { countryCode: 'BE', pointCountryCode: 'BE', pointType: 'POST_POINT' },
  });

  // Watch the customer countryCode so bpost (BE-only) delivery
  // options can be hidden when the buyer ships outside Belgium.
  const watchedCountry = (watch('countryCode') || 'BE').toUpperCase();
  const bpostCountrySupported = watchedCountry === 'BE';

  // Prefill profile + name + saved addresses
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');

  // ── Optional prescriber referral ─────────────────────────────────
  // Hidden behind a toggle. Loaded lazily the first time the toggle
  // is opened so we don't add a roundtrip to every checkout.
  const [prescriberToggle, setPrescriberToggle] = useState(false);
  const [prescribers, setPrescribers] = useState(null); // null = not loaded yet
  const [prescribersLoading, setPrescribersLoading] = useState(false);
  const [selectedPrescriberId, setSelectedPrescriberId] = useState('');

  const applyAddressToForm = (addr) => {
    if (!addr) return;
    setValue('street', addr.street || '', { shouldValidate: true });
    setValue('houseNumber', addr.houseNumber || '', { shouldValidate: true });
    setValue('bus', addr.bus || '');
    setValue('postalCode', addr.postalCode || '', { shouldValidate: true });
    setValue('city', addr.city || '', { shouldValidate: true });
    setValue('countryCode', addr.countryCode || 'BE');
    if (addr.firstName) setValue('customerFirstName', addr.firstName);
    if (addr.lastName) setValue('customerLastName', addr.lastName);
    if (addr.phone) setValue('customerPhone', addr.phone);
  };

  useEffect(() => {
    if (!user) return;
    accountApi.get().then(({ data }) => {
      const p = data.profile;
      const u = data.user || {};
      if (u.firstName) setValue('customerFirstName', u.firstName);
      if (u.lastName) setValue('customerLastName', u.lastName);
      if (u.phone) setValue('customerPhone', u.phone);
      // Prefer the new structured saved addresses when present.
      const addrs = Array.isArray(data.addresses) ? data.addresses : [];
      setSavedAddresses(addrs);
      const defaultShipping = addrs.find(a => a.isDefaultShipping
                                                && a.type !== 'BILLING')
                              || addrs.find(a => a.type !== 'BILLING')
                              || null;
      if (defaultShipping) {
        setSelectedAddressId(defaultShipping.id);
        applyAddressToForm(defaultShipping);
      } else if (p) {
        // Legacy flat-field fallback.
        if (p.street) setValue('street', p.street);
        if (p.number) setValue('houseNumber', p.number);
        if (p.bus) setValue('bus', p.bus);
        if (p.city) setValue('city', p.city);
        if (p.postalCode) setValue('postalCode', p.postalCode);
        if (p.country) setValue('countryCode', p.country);
        if (p.phone && !u.phone) setValue('customerPhone', p.phone);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, setValue]);

  const handleSelectAddress = (id) => {
    setSelectedAddressId(id);
    if (id === '__new__') return; // keep current form values for manual entry
    const addr = savedAddresses.find(a => a.id === id);
    if (addr) applyAddressToForm(addr);
  };

  const handleTogglePrescriber = () => {
    const next = !prescriberToggle;
    setPrescriberToggle(next);
    if (!next) {
      setSelectedPrescriberId('');
      return;
    }
    // Lazy-load the list the first time the toggle is enabled.
    if (prescribers === null && !prescribersLoading) {
      setPrescribersLoading(true);
      prescribersApi.list()
        .then(r => setPrescribers(Array.isArray(r.data) ? r.data : []))
        .catch(() => setPrescribers([]))
        .finally(() => setPrescribersLoading(false));
    }
  };

  // Live pricing reflecting real discount + free-shipping threshold.
  useEffect(() => {
    if (!items.length) return;
    cartApi.pricing().then(r => setDiscountInfo(r.data)).catch(() => {});
  }, [items]);

  // Lock pointType to the chosen method when the user is allowed to
  // edit pickup fields. Also clear point-* fields when the user
  // switches to HOME — required so we never submit stale pickup data
  // for a home-delivery order.
  useEffect(() => {
    if (deliveryMethod === 'PARCEL_LOCKER') setValue('pointType', 'PARCEL_LOCKER');
    else if (deliveryMethod === 'PICKUP_POINT') setValue('pointType', 'POST_POINT');
    else {
      // HOME: clear every pickup field so stale state never leaks.
      setValue('pointId', '');
      setValue('pointName', '');
      setValue('pointType', '');
      setValue('pointStreet', '');
      setValue('pointHouseNumber', '');
      setValue('pointBus', '');
      setValue('pointPostalCode', '');
      setValue('pointCity', '');
      setValue('pointCountryCode', '');
    }
  }, [deliveryMethod, setValue]);

  // If the buyer switches the destination country to a non-Belgian
  // one while having a bpost method selected, snap back to HOME so we
  // never submit a BE-only carrier for an international ship-to. The
  // selector itself is also hidden in that branch.
  useEffect(() => {
    if (!bpostCountrySupported && deliveryMethod !== 'HOME') {
      setDeliveryMethod('HOME');
    }
  }, [bpostCountrySupported, deliveryMethod]);

  // Probe bpost availability once on mount.
  useEffect(() => {
    let cancelled = false;
    checkoutApi.addressAvailability()
      .then(r => { if (!cancelled) setBpostAvailable(Boolean(r.data?.available)); })
      .catch(() => { if (!cancelled) setBpostAvailable(false); });
    return () => { cancelled = true; };
  }, []);

  const handleValidateAddress = async () => {
    setBpostBusy(true);
    setBpostStatus(null);
    setBpostSuggestion(null);
    try {
      const v = getValues();
      const { data } = await checkoutApi.addressValidate({
        street: v.street, houseNumber: v.houseNumber, bus: v.bus,
        postalCode: v.postalCode, city: v.city,
        countryCode: v.countryCode || 'BE',
      });
      if (!data?.available) setBpostStatus('unavailable');
      else if (!data?.ok) setBpostStatus(data?.error || 'error');
      else {
        setBpostStatus(data.result?.validationStatus || 'unvalidated');
        setBpostSuggestion(data.result || null);
      }
    } finally {
      setBpostBusy(false);
    }
  };

  const acceptBpostSuggestion = () => {
    if (!bpostSuggestion) return;
    setValue('street', bpostSuggestion.street ?? '', { shouldValidate: true });
    setValue('houseNumber', bpostSuggestion.houseNumber ?? '', { shouldValidate: true });
    setValue('bus', bpostSuggestion.bus ?? '');
    setValue('postalCode', bpostSuggestion.postalCode ?? '', { shouldValidate: true });
    setValue('city', bpostSuggestion.city ?? '', { shouldValidate: true });
    if (bpostSuggestion.countryCode) setValue('countryCode', bpostSuggestion.countryCode);
    setBpostSuggestion(null);
    setBpostStatus('applied');
  };

  const onSubmit = async (data) => {
    // Legal acceptance gate (2026-05-19). Defence-in-depth alongside
    // the disabled-button check — a tampered DOM can re-enable the
    // button, but the in-handler check still refuses to proceed.
    if (!legalAccepted) {
      setError(
        'Aanvaard eerst de algemene voorwaarden en het privacybeleid '
        + 'om verder te gaan.',
      );
      return;
    }
    // Delivery-method availability gate (2026-05-25). Refuses
    // pickup/locker when the runtime flag is OFF or the country is
    // not BE. The backend re-enforces this with HTTP 400
    // `delivery_method_not_available` so a tampered call still fails.
    if (isPickupMethod && !pickupAllowedForCurrentUser) {
      setError(
        'Deze verzendmethode is momenteel niet beschikbaar. Kies '
        + 'thuislevering om verder te gaan.',
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    setCartDiff(null);
    try {
      const payload = {
        street: data.street,
        houseNumber: data.houseNumber,
        number: data.houseNumber, // legacy alias for older consumers
        bus: data.bus || '',
        postalCode: data.postalCode,
        city: data.city,
        countryCode: data.countryCode || 'BE',
        guestEmail: user ? '' : (data.email || ''),
        notes: data.notes || '',
        customerFirstName: data.customerFirstName || '',
        customerLastName: data.customerLastName || '',
        customerPhone: data.customerPhone || '',
        deliveryMethod,
        carrier: 'BPOST',
        prescriberId: prescriberToggle ? selectedPrescriberId : '',
      };
      if (deliveryMethod !== 'HOME') {
        payload.deliveryPoint = {
          pointId: data.pointId,
          pointType: data.pointType,
          pointName: data.pointName,
          address: {
            street: data.pointStreet,
            houseNumber: data.pointHouseNumber,
            houseNumberAddition: data.pointBus || '',
            postalCode: data.pointPostalCode,
            city: data.pointCity,
            countryCode: data.pointCountryCode || 'BE',
          },
        };
      }
      const { data: result } = await checkoutApi.create(payload);
      clearCart();
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      } else {
        // Test-mode fallback: backend already finalized the paid order
        // and assigned the canonical orderNumber. If for any reason it
        // is missing (defensive), fall back to the pendingReference so
        // the pending page can resolve it.
        const tokenQS = result.accessToken ? `?token=${encodeURIComponent(result.accessToken)}` : '';
        const target = result.orderNumber || result.pendingReference;
        navigate(`/order/${target}/confirmation${tokenQS}`);
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      // Pre-cutover lock: the backend refused checkout because
      // commerce is not live. Swap to the full notice view.
      if (status === 503 && detail && typeof detail === 'object' && detail.code === 'commerce_not_live') {
        setCommerceLive(false);
        return;
      }
      // P0-4: a 409 cart_pricing_changed carries a structured detail
      // object ({code, message, items[]}). React refuses to render an
      // object as a child, so a naïve `setError(detail)` would white-
      // screen. We extract the localized message and surface a
      // structured banner with the diff so the customer can review
      // the changes before retrying.
      if (status === 409 && detail && typeof detail === 'object' && detail.code === 'cart_pricing_changed') {
        setCartDiff(detail);
        setError(detail.message || 'Je winkelmandje is gewijzigd. Vernieuw en bevestig opnieuw.');
        // Refresh the local cart view so the new prices are reflected.
        try {
          await reloadCart();
        } catch (_) { /* non-blocking — banner is the source of truth */ }
      } else {
        const msg = (typeof detail === 'string' && detail) ||
          (detail && typeof detail === 'object' && detail.message) ||
          'Er is een fout opgetreden bij het plaatsen van uw bestelling.';
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Pre-cutover lock view — replaces the checkout entirely (also on
  // direct /checkout visits). Storefront and cart stay browsable.
  if (commerceLive === false) return (
    <div data-testid="checkout-precutover" className="max-w-2xl mx-auto px-6 md:px-10 py-16">
      <div className="rounded-2xl border border-border bg-card px-8 py-12 text-center shadow-sm">
        <Store className="w-10 h-10 text-accent mx-auto mb-5" aria-hidden="true" />
        <h1 className="font-serif text-3xl md:text-4xl font-light text-foreground mb-3">{t.precutover.title}</h1>
        <div className="w-10 h-px bg-accent mx-auto mb-6" />
        <p data-testid="precutover-message" className="text-base text-muted-foreground leading-relaxed max-w-lg mx-auto mb-8">
          {t.precutover.message}
        </p>
        <a
          data-testid="precutover-current-shop-cta"
          href="https://www.theherborist.be/"
          className="inline-flex items-center justify-center gap-2.5 px-8 py-3.5 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90 transition-all active:scale-95 shadow-sm"
        >
          {t.precutover.cta} <ExternalLink className="w-4 h-4" />
        </a>
        <div className="mt-6">
          <Link to="/catalog" data-testid="precutover-browse-link" className="text-sm text-primary font-medium hover:underline">
            {t.precutover.browse}
          </Link>
        </div>
      </div>
    </div>
  );

  if (cartLoading || commerceLive === null) return (
    <div className="max-w-4xl mx-auto px-6 py-24 text-center">
      <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin mx-auto" />
    </div>
  );

  if (!items.length) return (
    <div className="max-w-4xl mx-auto px-6 py-24 text-center">
      <p className="font-serif text-xl text-muted-foreground mb-4">Uw winkelmand is leeg</p>
      <Link to="/catalog" className="text-sm text-primary font-medium hover:underline">Terug naar producten</Link>
    </div>
  );

  const isPickupMethod = deliveryMethod !== 'HOME';
  // Pickup section is rendered when one of:
  //  - admin / ?testMode=1 (Testmodus badge)
  //  - customer flag on + destination is BE
  const pickupSectionVisible = isPickupMethod
    && (manualPickupAllowed || (pickupLockerVisible && bpostCountrySupported));
  // Shared customer-availability boolean used by both the submit
  // button disabled-state and the onSubmit defence-in-depth check.
  // True means: either the user did not pick pickup/locker, or pickup
  // /locker is currently allowed for this customer (admin/testMode,
  // or runtime flag ON + destination BE).
  const pickupAllowedForCurrentUser = (!isPickupMethod)
    || manualPickupAllowed
    || (pickupLockerVisible && bpostCountrySupported);
  const addressSectionTitle = isPickupMethod
    ? 'Contact- en facturatieadres'
    : 'Leveringsadres';

  return (
    <div data-testid="checkout-page" className="max-w-5xl mx-auto px-6 md:px-10 py-10">
      <Link to="/cart" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-6 font-medium">
        <ArrowLeft className="w-4 h-4" /> Terug naar winkelmand
      </Link>
      <h1 className="font-serif text-3xl md:text-4xl font-light text-foreground mb-8">Afrekenen</h1>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 lg:gap-12 items-start">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-7" data-testid="checkout-form">

          {/* ─── Delivery method ────────────────────────────────── */}
          <section className="space-y-2" data-testid="delivery-method-section">
            <h2 className="font-serif text-lg font-medium text-foreground">Verzendmethode</h2>
            <div
              role="radiogroup"
              aria-label="Verzendmethode"
              className="rounded-xl border border-border overflow-hidden divide-y divide-border bg-card"
            >
              {DELIVERY_OPTIONS.map(opt => {
                const isBpostOption = opt.value !== 'HOME';
                // Hide BE-only bpost methods entirely for non-BE
                // destinations. HOME is the only universal option.
                if (isBpostOption && !bpostCountrySupported) return null;
                // Customer-visible enablement: when the runtime flag
                // `pickupLockerCustomerVisible` is on AND the
                // destination is BE, normal customers may select
                // pickup/locker. Admin + ?testMode=1 still always
                // allowed regardless of the flag.
                const customerVisible = isBpostOption && pickupLockerVisible && bpostCountrySupported;
                const enabled = opt.available || manualPickupAllowed || customerVisible;
                const selected = deliveryMethod === opt.value;
                const Icon = opt.Icon;
                const inputId = `delivery-method-input-${opt.value.toLowerCase()}`;
                return (
                  <label
                    key={opt.value}
                    htmlFor={inputId}
                    data-testid={`delivery-method-${opt.value.toLowerCase()}`}
                    aria-disabled={!enabled}
                    className={`flex items-center gap-3 px-3.5 py-2.5 transition-colors focus-within:ring-2 focus-within:ring-ring focus-within:ring-inset ${
                      selected
                        ? 'bg-primary/5'
                        : enabled
                          ? 'hover:bg-muted/50 cursor-pointer'
                          : 'opacity-60 cursor-not-allowed'
                    }`}
                  >
                    <input
                      id={inputId}
                      type="radio"
                      name="deliveryMethod"
                      value={opt.value}
                      checked={selected}
                      disabled={!enabled}
                      onChange={() => enabled && setDeliveryMethod(opt.value)}
                      className="w-4 h-4 accent-primary shrink-0"
                    />
                    <Icon
                      aria-hidden="true"
                      className={`w-4 h-4 shrink-0 ${selected ? 'text-primary' : 'text-muted-foreground'}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground leading-tight truncate">
                        {opt.label}
                      </p>
                      <p className="text-xs text-muted-foreground leading-tight truncate">
                        {opt.description}
                      </p>
                    </div>
                    {!opt.available && !manualPickupAllowed && !customerVisible && (
                      <span
                        data-testid={`delivery-method-${opt.value.toLowerCase()}-soon`}
                        className="shrink-0 text-[10px] tracking-wide uppercase font-semibold text-amber-700 bg-amber-100/70 px-1.5 py-0.5 rounded"
                      >
                        Binnenkort
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </section>

          {/* ─── Customer identity (always visible) ───────────── */}
          <section className="space-y-4" data-testid="customer-identity-section">
            <h2 className="font-serif text-lg font-medium text-foreground">Uw gegevens</h2>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Voornaam" error={errors.customerFirstName}>
                <input data-testid="checkout-first-name" {...register('customerFirstName', { required: 'Voornaam is verplicht' })} className={inputClass} />
              </Field>
              <Field label="Familienaam" error={errors.customerLastName}>
                <input data-testid="checkout-last-name" {...register('customerLastName', { required: 'Familienaam is verplicht' })} className={inputClass} />
              </Field>
            </div>
            {!user && (
              <Field label="E-mailadres" error={errors.email}>
                <input data-testid="checkout-email" type="email" {...register('email', { required: 'E-mail is verplicht' })} className={inputClass} placeholder="u@voorbeeld.be" />
              </Field>
            )}
            <Field label="Telefoonnummer (optioneel)">
              <input data-testid="checkout-phone" type="tel" {...register('customerPhone')} className={inputClass} placeholder="+32 ..." />
            </Field>
          </section>

          {/* ─── Address (label depends on method) ────────────── */}
          <section className="space-y-4">
            <div className="flex items-baseline justify-between">
              <h2 data-testid="address-section-title" className="font-serif text-lg font-medium text-foreground">
                {addressSectionTitle}
              </h2>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5" />
                Straat, huisnummer en bus apart invullen voor correcte verzending.
              </p>
            </div>
            {user && savedAddresses.length > 1 && (
              <div data-testid="saved-address-selector" className="rounded-xl border border-border bg-muted/30 px-3 py-2.5 space-y-2">
                <p className="text-xs font-medium text-foreground">Bewaarde adressen</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {savedAddresses.map(a => {
                    const checked = selectedAddressId === a.id;
                    return (
                      <label key={a.id}
                        data-testid={`saved-address-${a.id}`}
                        className={`flex items-start gap-2 px-2.5 py-2 rounded-lg border text-xs cursor-pointer transition-colors ${
                          checked ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'
                        }`}>
                        <input type="radio" name="savedAddress" value={a.id}
                          checked={checked} onChange={() => handleSelectAddress(a.id)}
                          className="mt-0.5 w-3.5 h-3.5 accent-primary shrink-0" />
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground truncate">{a.label || `${a.firstName} ${a.lastName}`.trim() || 'Adres'}</p>
                          <p className="text-muted-foreground truncate">{a.street} {a.houseNumber}{a.bus ? ` bus ${a.bus}` : ''}</p>
                          <p className="text-muted-foreground truncate">{a.postalCode} {a.city} · {a.countryCode}</p>
                        </div>
                      </label>
                    );
                  })}
                  <label data-testid="saved-address-new"
                    className={`flex items-start gap-2 px-2.5 py-2 rounded-lg border text-xs cursor-pointer transition-colors ${
                      selectedAddressId === '__new__' ? 'border-primary bg-primary/5' : 'border-dashed border-border hover:bg-muted/50'
                    }`}>
                    <input type="radio" name="savedAddress" value="__new__"
                      checked={selectedAddressId === '__new__'}
                      onChange={() => handleSelectAddress('__new__')}
                      className="mt-0.5 w-3.5 h-3.5 accent-primary shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground">Nieuw adres gebruiken</p>
                      <p className="text-muted-foreground">Manueel invullen hieronder.</p>
                    </div>
                  </label>
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Field label="Straat" error={errors.street}>
                  <input data-testid="checkout-street" {...register('street', { required: true })} className={inputClass} />
                </Field>
              </div>
              <Field label="Huisnummer" error={errors.houseNumber}>
                <input data-testid="checkout-house-number" {...register('houseNumber', { required: true })} className={inputClass} />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Bus / appt.">
                <input data-testid="checkout-bus" {...register('bus')} className={inputClass} placeholder="optioneel" />
              </Field>
              <Field label="Postcode" error={errors.postalCode}>
                <input data-testid="checkout-postal" {...register('postalCode', { required: true })} className={inputClass} />
              </Field>
              <Field label="Stad" error={errors.city}>
                <input data-testid="checkout-city" {...register('city', { required: true })} className={inputClass} />
              </Field>
            </div>
            <div className="max-w-[260px]">
              <Field label="Land">
                <select
                  data-testid="checkout-country"
                  {...register('countryCode')}
                  className={inputClass}
                >
                  <option value="BE">België</option>
                  <option value="NL">Nederland</option>
                  <option value="FR">Frankrijk</option>
                  <option value="LU">Luxemburg</option>
                </select>
              </Field>
              <p
                data-testid="checkout-country-scope-note"
                className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed"
              >
                Online bestellen is momenteel mogelijk voor België,
                Nederland, Frankrijk en Luxemburg. Voor andere
                EU-landen kunt u ons contacteren via{' '}
                <a
                  className="underline"
                  href="mailto:info@theherborist.be"
                >
                  info@theherborist.be
                </a>
                .
              </p>
            </div>

            {bpostAvailable && (
              <div className="rounded-xl border border-border bg-muted/40 px-4 py-3 space-y-2" data-testid="bpost-validation-card">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">bpost adresvalidatie (optioneel)</p>
                    <p className="text-xs text-muted-foreground">Vult straat, huisnummer en bus apart in na uw bevestiging.</p>
                  </div>
                  <button type="button" data-testid="bpost-validate-btn" onClick={handleValidateAddress} disabled={bpostBusy}
                    className="px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold hover:bg-secondary/80 transition-all disabled:opacity-50">
                    {bpostBusy ? 'Bezig...' : 'Valideer adres'}
                  </button>
                </div>
                {bpostStatus === 'applied' && (
                  <p className="text-xs text-green-700" data-testid="bpost-status-applied">bpost-suggestie toegepast op straat / huisnummer / bus.</p>
                )}
                {bpostStatus && !['applied'].includes(bpostStatus) && !bpostSuggestion && (
                  <p className="text-xs text-muted-foreground" data-testid="bpost-status">Status: {bpostStatus} — u kunt manueel doorgaan.</p>
                )}
                {bpostSuggestion && (
                  <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs space-y-1" data-testid="bpost-suggestion">
                    <p className="font-semibold text-foreground">Voorstel ({bpostSuggestion.validationStatus || 'suggestie'}):</p>
                    <p className="text-foreground">
                      {bpostSuggestion.street} {bpostSuggestion.houseNumber}{bpostSuggestion.bus ? ` bus ${bpostSuggestion.bus}` : ''}
                    </p>
                    <p className="text-foreground">{bpostSuggestion.postalCode} {bpostSuggestion.city} · {bpostSuggestion.countryCode}</p>
                    <div className="flex items-center gap-2 pt-1">
                      <button type="button" data-testid="bpost-apply-btn" onClick={acceptBpostSuggestion}
                        className="px-2.5 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90">Suggestie gebruiken</button>
                      <button type="button" data-testid="bpost-dismiss-btn"
                        onClick={() => { setBpostSuggestion(null); setBpostStatus('dismissed'); }}
                        className="px-2.5 py-1 rounded-full border border-border text-xs text-muted-foreground hover:bg-muted">Negeren</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* ─── Pickup point / parcel locker (test/admin OR customer-flag-on) ── */}
          {pickupSectionVisible && (
            <section className="space-y-4 border-t border-border pt-6" data-testid="pickup-section">
              <div className="flex items-baseline justify-between">
                <h2 className="font-serif text-lg font-medium text-foreground">
                  {deliveryMethod === 'PARCEL_LOCKER' ? 'bpost pakjesautomaat' : 'bpost afhaalpunt'}
                </h2>
                {manualPickupAllowed && (
                  <span data-testid="pickup-testmode-badge" className="text-[10px] tracking-wide uppercase font-semibold text-amber-700 bg-amber-100/70 px-1.5 py-0.5 rounded">
                    Testmodus
                  </span>
                )}
              </div>

              <BpostLocationSearch
                method={deliveryMethod}
                getDeliveryAddress={getValues}
                testMode={testMode}
                onSelect={(loc) => {
                  setValue('pointId', loc.pointId, { shouldValidate: true });
                  setValue('pointName', loc.name, { shouldValidate: true });
                  setValue('pointType', loc.type === 'parcel_locker' ? 'PARCEL_LOCKER' : 'POST_POINT');
                  setValue('pointStreet', loc.street || '', { shouldValidate: true });
                  setValue('pointHouseNumber', loc.houseNumber || '', { shouldValidate: true });
                  setValue('pointBus', '');
                  setValue('pointPostalCode', loc.postalCode || '', { shouldValidate: true });
                  setValue('pointCity', loc.city || '', { shouldValidate: true });
                  setValue('pointCountryCode', 'BE');
                }}
              />

              <p className="text-xs text-muted-foreground" data-testid="pickup-manual-fallback-note">
                Geen geschikt punt gevonden? Vul de gegevens hieronder manueel in (de officiële bpost API
                wordt aangesproken indien geconfigureerd; anders blijven onderstaande velden de bron).
              </p>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Referentie van afhaalpunt" error={errors.pointId} hint="Tijdelijke ID (bv. bpost-puntcode).">
                  <input data-testid="checkout-point-id" {...register('pointId', { required: pickupSectionVisible })} className={inputClass} placeholder="BE123456" />
                </Field>
                <Field label="Naam van punt" error={errors.pointName}>
                  <input data-testid="checkout-point-name" {...register('pointName', { required: pickupSectionVisible })} className={inputClass} />
                </Field>
              </div>
              <input type="hidden" {...register('pointType')} />
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <Field label="Straat" error={errors.pointStreet}>
                    <input data-testid="checkout-point-street" {...register('pointStreet', { required: pickupSectionVisible })} className={inputClass} />
                  </Field>
                </div>
                <Field label="Huisnummer" error={errors.pointHouseNumber}>
                  <input data-testid="checkout-point-house-number" {...register('pointHouseNumber', { required: pickupSectionVisible })} className={inputClass} />
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Bus / addendum">
                  <input data-testid="checkout-point-bus" {...register('pointBus')} className={inputClass} placeholder="optioneel" />
                </Field>
                <Field label="Postcode" error={errors.pointPostalCode}>
                  <input data-testid="checkout-point-postal" {...register('pointPostalCode', { required: pickupSectionVisible })} className={inputClass} />
                </Field>
                <Field label="Stad" error={errors.pointCity}>
                  <input data-testid="checkout-point-city" {...register('pointCity', { required: pickupSectionVisible })} className={inputClass} />
                </Field>
              </div>
              <div className="max-w-[160px]">
                <Field label="Land">
                  <input data-testid="checkout-point-country" {...register('pointCountryCode')} className={inputClass} />
                </Field>
              </div>
            </section>
          )}

          {/* ─── Pickup chosen but not unlocked → soft-launch notice ── */}
          {isPickupMethod && !pickupSectionVisible && (
            <section
              data-testid="pickup-coming-soon"
              className="border border-dashed border-border rounded-xl px-4 py-5 text-center bg-muted/30"
            >
              <p className="text-sm font-semibold text-foreground">
                {deliveryMethod === 'PARCEL_LOCKER'
                  ? 'bpost pakjesautomaat — binnenkort beschikbaar'
                  : 'bpost afhaalpunt — binnenkort beschikbaar'}
              </p>
              <p className="text-xs text-muted-foreground mt-1.5">
                We voltooien de bpost-keuzehulp eerstdaags. Kies tot dan
                Thuislevering om uw bestelling af te ronden.
              </p>
              <button
                type="button"
                data-testid="switch-to-home-btn"
                onClick={() => setDeliveryMethod('HOME')}
                className="mt-3 px-3.5 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90"
              >
                Schakel naar Thuislevering
              </button>
            </section>
          )}

          {/* ─── Optional prescriber referral ─────────────────── */}
          <section className="space-y-3" data-testid="prescriber-section">
            <label className="flex items-start gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="prescriber-toggle"
                checked={prescriberToggle}
                onChange={handleTogglePrescriber}
                className="mt-0.5 w-4 h-4 accent-primary shrink-0"
              />
              <span className="text-sm text-foreground">
                Heeft uw therapeut deze producten aanbevolen?
                <span className="block text-xs text-muted-foreground mt-0.5">
                  Optioneel — selecteer uw therapeut zodat wij uw bestelling kunnen koppelen.
                </span>
              </span>
            </label>
            {prescriberToggle && (
              <div data-testid="prescriber-picker" className="ml-7 space-y-1.5">
                {prescribersLoading && (
                  <p data-testid="prescriber-loading" className="text-xs text-muted-foreground">
                    Lijst laden...
                  </p>
                )}
                {!prescribersLoading && prescribers && prescribers.length === 0 && (
                  <p data-testid="prescriber-empty" className="text-xs text-muted-foreground">
                    Er zijn momenteel geen therapeuten beschikbaar in de lijst.
                  </p>
                )}
                {!prescribersLoading && prescribers && prescribers.length > 0 && (
                  <>
                    <label className="block text-xs font-medium text-foreground">Therapeut</label>
                    <select
                      data-testid="prescriber-select"
                      value={selectedPrescriberId}
                      onChange={(e) => setSelectedPrescriberId(e.target.value)}
                      className={inputClass}
                    >
                      <option value="">— Kies een therapeut —</option>
                      {prescribers.map(p => {
                        const fullName = `${p.firstName} ${p.lastName}`.trim();
                        const suffix = p.orgName ? ` — ${p.orgName}` : '';
                        return (
                          <option key={p.id} value={p.id} data-testid={`prescriber-option-${p.id}`}>
                            {fullName}{suffix}
                          </option>
                        );
                      })}
                    </select>
                  </>
                )}
              </div>
            )}
          </section>

          {/* ─── Notes ───────────────────────────────────────── */}
          <section>
            <Field label="Opmerkingen">
              <textarea {...register('notes')} rows={2} className={inputClass} placeholder="Bijzondere instructies (optioneel)" />
            </Field>
          </section>

          {cartDiff && Array.isArray(cartDiff.items) && cartDiff.items.length > 0 && (
            <div data-testid="cart-changed-banner" className="text-sm bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-lg space-y-2">
              <div className="font-semibold">Je winkelmandje is gewijzigd</div>
              <div className="text-xs">Onderstaande producten hebben een nieuwe prijs of BTW. Vernieuw je winkelmandje en bevestig opnieuw — er is nog geen betaling gestart.</div>
              <ul className="text-xs space-y-1">
                {cartDiff.items.map((it, idx) => (
                  <li key={it.productId || idx} data-testid="cart-changed-item" className="flex flex-wrap gap-x-3">
                    <span className="font-medium">{it.newName_nl || it.name || 'Onbekend product'}</span>
                    {it.changes?.price && (
                      <span>prijs: <s>€{Number(it.oldPrice).toFixed(2)}</s> → <strong>€{Number(it.newPrice).toFixed(2)}</strong></span>
                    )}
                    {it.changes?.vatRate && (
                      <span>BTW: <s>{Math.round(Number(it.oldVatRate) * 100)}%</s> → <strong>{Math.round(Number(it.newVatRate) * 100)}%</strong></span>
                    )}
                    {it.changes?.name_nl && !it.changes?.price && !it.changes?.vatRate && (
                      <span>naam gewijzigd</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {error && <div data-testid="checkout-error" className="text-sm text-destructive bg-destructive/10 px-4 py-2.5 rounded-lg">{error}</div>}

          {/* ─── Pre-payment legal notice + required acceptance ─── */}
          <div
            data-testid="checkout-legal-block"
            className="rounded-2xl border border-border bg-muted/30 px-4 py-4 text-xs leading-relaxed space-y-2"
          >
            <p className="text-foreground">
              Door op <strong>Bestelling plaatsen</strong> te klikken,
              start u de betaling en aanvaardt u onze{' '}
              <a
                data-testid="checkout-link-terms"
                href="/algemene-voorwaarden"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                algemene voorwaarden
              </a>
              {' '}en het{' '}
              <a
                data-testid="checkout-link-privacy"
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                privacybeleid
              </a>
              . Lees ook het{' '}
              <a
                data-testid="checkout-link-withdrawal"
                href="/herroepingsrecht"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                herroepingsrecht
              </a>
              {' '}en de{' '}
              <a
                data-testid="checkout-link-shipping"
                href="/verzending"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                informatie over verzending
              </a>
              .
            </p>
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                data-testid="checkout-accept-terms"
                type="checkbox"
                className="mt-0.5"
                checked={legalAccepted}
                onChange={(e) => setLegalAccepted(e.target.checked)}
              />
              <span className="text-foreground">
                Ik aanvaard de algemene voorwaarden en het
                privacybeleid, en bevestig dat ik de informatie over
                herroepingsrecht en levering heb gelezen.
              </span>
            </label>
          </div>

          <button
            data-testid="place-order-btn"
            type="submit"
            disabled={submitting || !legalAccepted || !pickupAllowedForCurrentUser}
            className="w-full py-3.5 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Bestelling plaatsen...</>) : 'Bestelling plaatsen'}
          </button>
        </form>

        {/* ─── Order summary (sticky on desktop) ─────────────── */}
        <aside className="lg:sticky lg:top-24 self-start">
          <h2 className="font-serif text-lg font-medium mb-3 text-foreground">Overzicht</h2>
          <div className="bg-muted/50 rounded-2xl p-5 space-y-3">
            {items.map(item => {
              const name = item[`nameSnapshot_${locale}`] || item.nameSnapshot_nl;
              const lc = discountInfo?.lineCalcs?.find(l => l.productId === item.productId) || {};
              const effectivePrice = lc.effectiveUnitPrice ?? item.priceSnapshot;
              const hasStack = (lc.freeUnits ?? 0) > 0;
              return (
                <div key={item._id} className="flex items-center justify-between text-sm">
                  <div>
                    <span className="text-foreground">{name} <span className="text-muted-foreground">× {item.quantity}</span></span>
                    {hasStack && <span className="ml-2 text-xs text-green-700">({lc.freeUnits} gratis)</span>}
                  </div>
                  <span className="font-semibold">{formatCurrency(effectivePrice * item.quantity, locale)}</span>
                </div>
              );
            })}
            {discountInfo?.totalStackSavingExclVat > 0 && (
              <div data-testid="checkout-stack-discount" className="flex justify-between text-sm text-green-700">
                <span>11+1 stapelkorting (excl. BTW)</span>
                <span>-{formatCurrency(discountInfo.totalStackSavingExclVat, locale)}</span>
              </div>
            )}
            {discountInfo?.discountType === 'profile' && (
              <div data-testid="checkout-profile-discount" className="flex justify-between text-sm text-primary">
                <span>Uw persoonlijke korting ({discountInfo.appliedRate}%)</span>
                <span>-{formatCurrency(discountInfo.discountExclVat, locale)}</span>
              </div>
            )}
            {discountInfo?.discountType === 'b2b_standard' && (
              <div data-testid="checkout-b2b-discount" className="flex justify-between text-sm text-primary">
                <span>B2B korting ({discountInfo.appliedRate}%)</span>
                <span>-{formatCurrency(discountInfo.discountExclVat, locale)}</span>
              </div>
            )}
            {discountInfo?.vatReversed && (
              <div data-testid="checkout-vat-reversed" className="rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-xs px-3 py-2">
                <p className="font-semibold">BTW verlegd — intracommunautaire levering, Art. 39bis W.BTW.</p>
                <p className="mt-0.5">Geen BTW verschuldigd.</p>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Verzendkosten</span>
              <span data-testid="checkout-shipping" className="font-medium">
                {discountInfo?.shippingCost > 0
                  ? formatCurrency(discountInfo.shippingCost, locale)
                  : 'Gratis'}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-2">
              Gratis verzending vanaf {formatCurrency(55, locale)}.
            </p>
            <div className="border-t border-border pt-3 flex items-center justify-between">
              <span className="font-serif text-base text-foreground">Totaal</span>
              <span data-testid="checkout-total" className="font-semibold text-base">
                {formatCurrency(discountInfo?.grandTotal ?? items.reduce((s, i) => s + i.priceSnapshot * i.quantity, 0), locale)}
              </span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
